/**
 * CSV Exporter Service Contract
 * Handles marketplace-specific CSV generation and download functionality
 */

import type { Batch } from './storageService'

export interface ExportOptions {
  includeEditedOnly?: boolean
  categoryFilter?: string
  confidenceThreshold?: number
  keywordLimit?: number
  customFields?: Record<string, string>
}

export interface ExportResult {
  id: string
  marketplace: MarketplaceType
  filename: string
  content: string
  rowCount: number
  createdAt: Date
  exportedAt: Date
  downloadUrl: string
}

export interface MarketplaceSpec {
  name: string
  columns: ColumnSpec[]
  requirements: RequirementSpec
  formatting: FormattingSpec
}

export interface ColumnSpec {
  name: string
  required: boolean
  dataType: 'string' | 'number' | 'boolean' | 'array'
  maxLength?: number
  format?: string
  defaultValue?: string
}

export interface RequirementSpec {
  minKeywords?: number
  maxKeywords?: number
  maxTitleLength?: number
  maxDescriptionLength?: number
  requiredFields: string[]
  keywordSeparator: string
}

export interface FormattingSpec {
  encoding: string
  delimiter: string
  quote: string
  escape: string
  header: boolean
  dateFormat: string
}

export type MarketplaceType = 'shutterstock' | 'adobe' | 'adobe-stock' | 'istock' | 'pond5' | 'custom'

/**
 * CSV Exporter Service Interface
 * All implementations must adhere to this contract
 */
export interface CSVExporter {
  /**
   * Exports batch data to Shutterstock CSV format
   * @param batch - Batch data to export
   * @param options - Export options and filters
   * @returns Promise resolving to CSV content string
   * @throws ExportError if generation fails
   */
  exportToShutterstock(batch: Batch, options?: ExportOptions): Promise<string>

  /**
   * Exports batch data to Adobe Stock CSV format
   * @param batch - Batch data to export
   * @param options - Export options and filters
   * @returns Promise resolving to CSV content string
   * @throws ExportError if generation fails
   */
  exportToAdobe(batch: Batch, options?: ExportOptions): Promise<string>

  /**
   * Exports batch data to iStock CSV format
   * @param batch - Batch data to export
   * @param options - Export options and filters
   * @returns Promise resolving to CSV content string
   * @throws ExportError if generation fails
   */
  exportToiStock(batch: Batch, options?: ExportOptions): Promise<string>

  /**
   * Exports batch data to Pond5 CSV format
   * @param batch - Batch data to export
   * @param options - Export options and filters
   * @returns Promise resolving to CSV content string
   * @throws ExportError if generation fails
   */
  exportToPond5(batch: Batch, options?: ExportOptions): Promise<string>

  /**
   * Generic export method for any marketplace
   * @param batch - Batch data to export
   * @param marketplace - Target marketplace type
   * @param options - Export options and filters
   * @returns Promise resolving to ExportResult
   * @throws ExportError if generation fails
   */
  exportToMarketplace(
    batch: Batch,
    marketplace: MarketplaceType,
    options?: ExportOptions
  ): Promise<ExportResult>

  /**
   * Export directly from images array and metadata (bypasses batch structure)
   * @param images - Array of image files
   * @param imageMetadata - Record mapping image IDs to metadata
   * @param marketplace - Target marketplace type
   * @param sessionName - Optional session name for filename
   * @returns Promise resolving to ExportResult
   * @throws ExportError if generation fails
   */
  exportFromImages(
    images: any[],
    imageMetadata: Record<string, any>,
    marketplace: MarketplaceType,
    sessionName?: string
  ): Promise<ExportResult>

  /**
   * Triggers browser download of CSV content
   * @param content - CSV content string
   * @param filename - Suggested filename for download
   * @returns Promise resolving when download initiated
   */
  downloadCSV(content: string, filename: string): Promise<void>

  /**
   * Validates batch data against marketplace requirements
   * @param batch - Batch to validate
   * @param marketplace - Target marketplace
   * @returns Validation result with errors/warnings
   */
  validateForMarketplace(
    batch: Batch,
    marketplace: MarketplaceType
  ): ValidationResult

  /**
   * Gets marketplace specification details
   * @param marketplace - Target marketplace
   * @returns Marketplace specification object
   */
  getMarketplaceSpec(marketplace: MarketplaceType): MarketplaceSpec

  /**
   * Previews export data without generating full CSV
   * @param batch - Batch data to preview
   * @param marketplace - Target marketplace
   * @param rowLimit - Maximum rows to include in preview
   * @returns Promise resolving to preview data
   */
  previewExport(
    batch: Batch,
    marketplace: MarketplaceType,
    rowLimit?: number
  ): Promise<ExportPreview>

  /**
   * Gets suggested filename for export
   * @param batch - Source batch
   * @param marketplace - Target marketplace
   * @returns Suggested filename with extension
   */
  generateFilename(batch: Batch, marketplace: MarketplaceType): string
}

export interface ExportPreview {
  headers: string[]
  rows: string[][]
  totalRowCount: number
  truncated: boolean
  marketplace: MarketplaceType
}

/**
 * Error types thrown by CSVExporter implementations
 */
export class ExportError extends Error {
  constructor(
    message: string,
    public code: string,
    public marketplace?: MarketplaceType,
    public details?: Record<string, any>,
  ) {
    super(message)
    this.name = 'ExportError'
  }
}

/**
 * Export error codes
 */
export const EXPORT_CODES = {
  INVALID_BATCH_DATA: 'INVALID_BATCH_DATA',
  MISSING_REQUIRED_FIELDS: 'MISSING_REQUIRED_FIELDS',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  FORMATTING_ERROR: 'FORMATTING_ERROR',
  DOWNLOAD_FAILED: 'DOWNLOAD_FAILED',
  UNSUPPORTED_MARKETPLACE: 'UNSUPPORTED_MARKETPLACE',
  EMPTY_BATCH: 'EMPTY_BATCH',
  SIZE_LIMIT_EXCEEDED: 'SIZE_LIMIT_EXCEEDED',
  EXPORT_GENERATION_FAILED: 'EXPORT_GENERATION_FAILED',
} as const

/**
 * Marketplace-specific CSV column mappings
 */
export const MARKETPLACE_COLUMNS = {
  shutterstock: [
    'Filename',
    'Description',
    'Keywords',
    'Categories',
    'Editorial',
    'Mature content',
    'illustration',
  ],
  adobe: [
    'Filename',
    'Title',
    'Keywords',
    'Description',
    'Category',
    'Model Release',
    'Property Release',
  ],
  istock: ['Filename', 'Keywords', 'Description', 'Category', 'Exclusive'],
  pond5: ['Filename', 'Title', 'Description', 'Keywords', 'Tags', 'Category'],
} as const

/**
 * Marketplace requirements
 */
export const MARKETPLACE_REQUIREMENTS = {
  shutterstock: {
    minKeywords: 7,
    maxKeywords: 50,
    maxDescriptionLength: 200,
    keywordSeparator: ',',
    requiredFields: ['Filename', 'Description', 'Keywords'],
  },
  adobe: {
    maxKeywords: 49,
    maxTitleLength: 200,
    maxDescriptionLength: 2000,
    keywordSeparator: ';',
    requiredFields: ['Filename', 'Title', 'Keywords'],
  },
  'adobe-stock': {
    minKeywords: 7,
    maxKeywords: 50,
    maxDescriptionLength: 200,
    keywordSeparator: ',',
    requiredFields: ['Filename', 'Description', 'Keywords'],
  },
  istock: {
    maxKeywords: 50,
    maxTitleLength: 70,
    keywordSeparator: ',',
    requiredFields: ['Filename', 'Title', 'Keywords'],
  },
  pond5: {
    maxKeywords: 50,
    keywordSeparator: ',',
    requiredFields: ['Filename', 'Title', 'Keywords'],
  },
  custom: {
    keywordSeparator: ',',
    requiredFields: ['Filename', 'Title', 'Keywords'],
  },
} as const

export type MarketplaceRequirements = typeof MARKETPLACE_REQUIREMENTS

/**
 * Common validation result interfaces
 */
export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
}

export interface ValidationError {
  field: string
  message: string
  value?: any
  name?: string
  code?: string
  rowIndex?: number
}

export interface ValidationWarning {
  field: string
  message: string
  suggestion?: string
  code?: string
  severity?: string
  rowIndex?: number
}
